import { Controller, useFormContext, type FieldValues } from 'react-hook-form';

import { Select as PayflowUISelect, Skeleton } from '@ctct/payflow-ui';

import { Field } from './Field';
import { useFormLoadingContext } from './FormLoadingContext';
import { errorAdapter } from './helpers';
import type { PropsBase } from './types';

interface FormFieldSelectProps<T extends FieldValues> extends PropsBase<T> {
  allowEmpty?: boolean;
  disabled?: boolean;
  items: {
    text: string;
    key: string;
    value: string;
    disabled?: boolean;
    hidden?: boolean;
  }[];
}

export function FormFieldSelectNonFekit<T extends FieldValues>({
  label = '',
  name,
  qeId,
  optional,
  items,
  disableMarginBottom,
  placeholder,
  disabled,
  allowEmpty = false,
}: FormFieldSelectProps<T>) {
  const id = `form-field-${name}`;
  const isLoading = useFormLoadingContext();

  const {
    control,
    formState: { errors },
  } = useFormContext<T>();

  const error = errors[name];

  return (
    <Field
      data-qe-id={qeId}
      label={label}
      labelFor={id}
      error={errorAdapter(error)}
      category={optional ? 'optional' : undefined}
      style={{ marginBottom: disableMarginBottom ? 0 : undefined }}
    >
      {isLoading ? (
        <Skeleton height={35} />
      ) : (
        <Controller
          control={control}
          name={name}
          render={({ field }) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, @typescript-eslint/no-shadow
            const { ref, onChange, onBlur, ...restField } = field;

            return (
              <PayflowUISelect
                fullWidth
                variant="rounded"
                data-qe-id={qeId.concat('-select')}
                invalid={!!error}
                options={items.map((item) => ({
                  value: item.value,
                  label: item.text,
                }))}
                onValueChange={(...args) => {
                  onChange(...args);
                  // We use mode: 'onTouched' in useForm, so we need to manually trigger onBlur
                  // to ensure that the field is marked as touched and the error can be revalidated
                  onBlur();
                }}
                placeholder={placeholder}
                defaultValue={allowEmpty ? '' : undefined}
                disabled={disabled}
                {...restField}
              />
            );
          }}
        />
      )}
    </Field>
  );
}
